import {
  GTMEventsEnum,
  GTMSAuthEnum,
} from "common/google-tag-manager/constants";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import { ServerErrors, isEmailValid } from "dash-common";
import {
  Button,
  Flex,
  Form,
  FormErrorTypes,
  Input,
  Text,
  ViewerContext,
  localStorage,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
} from "dash-ui";
import { RoutesPath } from "flow-components/routes/routes";
import {
  GetCurrentBrokerDocument,
  useAuthorizationMutation,
  useGetCurrentBrokerLazyQuery,
} from "gql/generated.graphql";
import React, { FormEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
enum FormFields {
  Email = "email",
  Password = "password",
}

const FormFieldsList = [FormFields.Email, FormFields.Password];

function SignInForm() {
  const { t } = useTranslation(["forms", "common"]);
  const { account, setAccount } = useContext(ViewerContext);
  const [formErrors, , [setEmailError, setPasswordError]] =
    useFormValidation(FormFieldsList);
  const navigate = useNavigate();
  const translateErrorMessage = useServerResponseTranslation();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect");

  const [
    formState,
    [setEmail, setPassword],
    [formOnSubmit, formOnError, formOnSuccess],
  ] = useFormState(FormFieldsList);

  const [authMutation, { loading: authLoading }] = useAuthorizationMutation();
  const [isLoading, setIsLoading] = useState(false);
  const loading = authLoading || isLoading;

  const validEmail = (value: string) => {
    if (!value) {
      setEmailError({
        type: FormErrorTypes.Invalid,
        message: t`messages.emailRequired`,
      });
      return false;
    }
    if (!isEmailValid(value)) {
      setEmailError({
        type: FormErrorTypes.Invalid,
        message: t`messages.emailInvalid`,
      });
      return false;
    }
    setEmailError(null);
    return true;
  };

  const validPassword = (value: string) => {
    if (!value) {
      setPasswordError({
        type: FormErrorTypes.Invalid,
        message: t`messages.passwordRequired`,
      });
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const onEmailTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    validEmail(e.target.value);
  };

  const onPasswordTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPassword(value);
    validPassword(value);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { email, password } = formState;
    if ([validEmail(email), validPassword(password)].filter((v) => !v).length) {
      return null;
    }
    authMutation({
      variables: { email, password },
      onError(error) {
        if (error.message === ServerErrors.BROKER_ACCOUNT_ARCHIVED) {
          const exception = error.graphQLErrors[0]?.extensions
            ?.exception as any;
          formOnError(
            t(
              `common:brokerPortalServerErrors.${ServerErrors.BROKER_ACCOUNT_ARCHIVED}`,
              { reason: exception?.response?.payload }
            )
          );
        } else {
          formOnError(
            translateErrorMessage(error, ["common:brokerPortalServerErrors"])
          );
        }

        setIsLoading(false);
        setEmailError({ type: FormErrorTypes.Invalid, message: "" });
        setPasswordError({ type: FormErrorTypes.Invalid, message: "" });
      },
      onCompleted: async (data) => {
        const { authorization } = data;
        const { accessToken, refreshToken, refreshTokenExpiry } = authorization;
        // TODO: this should be better handled: we need to track down the event status
        localStorage.setAccessToken(accessToken);
        localStorage.setRefreshToken(refreshToken);
        localStorage.setRefreshTokenExpiry(refreshTokenExpiry || "");
        window.dispatchEvent(new Event("storage"));
        const { email } = formState;
        createGTMEvent({
          dataLayer: {
            event: GTMEventsEnum.SIGN_IN,
            user_id: getGoogleTagManagerUserId(email),
            method: GTMSAuthEnum.EMAIL,
          },
        });
        formOnSuccess();
        // loading state until localStorage event gets handled
        setIsLoading(true);
      },
    });
    return null;
  };
  
  // redirect to home page effect
  useEffect(() => {
    if (account) {
      // Redirect if has redirect param
      if (redirectParam) {
        navigate(redirectParam);
        return;
      }
      navigate(RoutesPath.Dashboard);
    }
  }, [account, navigate]);

  return (
    <Form id="sign-in-form" className="sign-in-form" onSubmit={onSubmit} w={334}>
      <Form.Group mt={1}>
        <Form.Label>{t`labels.email`}</Form.Label>
        <Input
          w="100%"
          type="email"
          placeholder={t`placeholders.email` || ""}
          onChange={onEmailTextChange}
          $errorType={formErrors[FormFields.Email]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Email]?.type}>
          {formErrors[FormFields.Email]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={1}>
        <Flex justifyContent="space-between">
          <Flex flex={1}>
            <Form.Label>{t`labels.password`}</Form.Label>
          </Flex>
          <Flex>
            <Form.Label>
              <Link
                to={RoutesPath.ForgotPassword}>{t`labels.forgotPassword`}</Link>
            </Form.Label>
          </Flex>
        </Flex>
        <Form.InputPassword
          w="100%"
          placeholder={t`placeholders.password` || ""}
          onChange={onPasswordTextChange}
          $errorType={formErrors[FormFields.Password]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Password]?.type}>
          {formErrors[FormFields.Password]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Button w="100%" type="submit" disabled={loading} isLoading={loading}>
          {t`labels.signIn`}
        </Button>
      </Form.Group>
      <Button
        w="100%"
        type="button"
        variation="outline"
        icon="miamiRealtorsIcon"
        mt={4}
        onClick={() => {
          const restApiDomain = process.env["REACT_APP_REST"];
          window.open(`${restApiDomain}/auth/login-sso`, "_self");
        }}
        disabled={loading}
        isLoading={loading}>
        Login with MIAMI REALTORS
      </Button>
      <Flex mt={4}>
        <Flex>
          <Text>
            {t("pages:signInPage:bottomLabel")}{" "}
            <Link to={RoutesPath.SignUp}>
              {t("pages:signInPage:bottomLabelLink")}
            </Link>
          </Text>
        </Flex>
      </Flex>
      <Form.Group mt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Form.Group>
    </Form>
  );
}

export default SignInForm;
